/**
 * Modal System
 * Handles opening/closing of native <dialog> modals with optional navigation
 */
export default class Modal {
	constructor() {
		this.isInitialized = false;
		this.init();
	}

	init() {
		if(this.isInitialized) return;
		this.isInitialized = true;

		// Open modal triggers
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('.modal-trigger');
			if(!trigger) return;

			e.preventDefault();
			const modalId = trigger.dataset.modal;
			if(modalId) {
				this.open(modalId);
			}
		});

		// Close modal handlers
		document.addEventListener('click', (e) => {
			const closeBtn = e.target.closest('.modal-close');
			if(!closeBtn) return;

			e.preventDefault();
			const modal = closeBtn.closest('dialog');
			if(modal?.id) {
				this.close(modal.id);
			}
		});

		// Navigation: Previous
		document.addEventListener('click', (e) => {
			const button = e.target.closest('.modal-nav-prev');
			if(!button) return;

			e.preventDefault();
			const currentModal = button.closest('dialog');
			if(currentModal) {
				this.navigatePrevious(currentModal);
			}
		});

		// Navigation: Next
		document.addEventListener('click', (e) => {
			const button = e.target.closest('.modal-nav-next');
			if(!button) return;

			e.preventDefault();
			const currentModal = button.closest('dialog');
			if(currentModal) {
				this.navigateNext(currentModal);
			}
		});

		// Close on backdrop click
		document.addEventListener('click', (e) => {
			const modal = e.target.closest('.op-modal');
			// Only close if clicking the modal itself (backdrop), not children
			if(modal && e.target === modal) {
				// Don't close if user is selecting text
				const selection = window.getSelection();
				if(selection?.toString().length > 0) {
					return;
				}
				this.close(modal.id);
			}
		});

		// Close on Escape key
		document.addEventListener('keydown', (e) => {
			if(e.key === 'Escape') {
				document.querySelectorAll('.op-modal[open]').forEach((modal) => {
					if(modal.id) {
						this.close(modal.id);
					}
				});
			}		});
	}

	// Public API methods for programmatic control
	open(modalId) {
		const modal = document.getElementById(modalId);
		if(!modal || typeof modal.showModal !== 'function') {
			console.warn(`Modal "${modalId}" not found or not a dialog element`);
			return false;
		}

		try {
			modal.showModal();
			// Trigger reflow to ensure transition works
			void modal.offsetHeight;
			modal.classList.add('is-open');

			if(modal.classList.contains('op-modal-nav')) {
				this.updateNavigationButtons(modal);
			}
			return true;
		} catch(error) {
			console.error(`Error opening modal "${modalId}":`, error);
			return false;
		}
	}

	close(modalId) {
		const modal = document.getElementById(modalId);
		if(!modal || typeof modal.close !== 'function') {
			return false;
		}

		modal.classList.remove('is-open');

		// Get transition duration from CSS or use default
		const duration = this.getTransitionDuration(modal) || 300;

		setTimeout(() => {
			try {
				modal.close();
			} catch(error) {
				console.error(`Error closing modal "${modalId}":`, error);
			}
		}, duration);

		return true;
	}

	closeAll() {
		document.querySelectorAll('.op-modal[open]').forEach((modal) => {
			if(modal.id) {
				this.close(modal.id);
			}
		});
	}

	// Navigation methods
	navigateNext(currentModal) {
		if(!currentModal) return;

		const nextModal = currentModal.nextElementSibling;
		if(nextModal?.matches('.op-modal-nav')) {
			const currentId = currentModal.id;
			const nextId = nextModal.id;

			if(!currentId || !nextId) return;

			this.close(currentId);

			const duration = this.getTransitionDuration(currentModal) || 300;
			setTimeout(() => {
				this.open(nextId);
			}, duration);
		}
	}

	navigatePrevious(currentModal) {
		if(!currentModal) return;

		const prevModal = currentModal.previousElementSibling;
		if(prevModal?.matches('.op-modal-nav')) {
			const currentId = currentModal.id;
			const prevId = prevModal.id;

			if(!currentId || !prevId) return;

			this.close(currentId);

			const duration = this.getTransitionDuration(currentModal) || 300;
			setTimeout(() => {
				this.open(prevId);
			}, duration);
		}
	}

	updateNavigationButtons(modal) {
		if(!modal?.classList.contains('op-modal-nav')) return;

		const prevBtn = modal.querySelector('.modal-nav-prev');
		const nextBtn = modal.querySelector('.modal-nav-next');

		if(!prevBtn || !nextBtn) return;

		// Check for previous sibling
		const prevModal = modal.previousElementSibling;
		if(prevModal?.matches('.op-modal-nav')) {
			prevBtn.style.display = '';
			prevBtn.disabled = false;
		} else {
			prevBtn.style.display = 'none';
			prevBtn.disabled = true;
		}

		// Check for next sibling
		const nextModal = modal.nextElementSibling;
		if(nextModal?.matches('.op-modal-nav')) {
			nextBtn.style.display = '';
			nextBtn.disabled = false;
		} else {
			nextBtn.style.display = 'none';
			nextBtn.disabled = true;
		}
	}

	// Helper to get CSS transition duration
	getTransitionDuration(element) {
		if(!element) return null;

		const style = window.getComputedStyle(element);
		const duration = style.transitionDuration || style.webkitTransitionDuration;

		if(!duration) return null;

		// Convert to milliseconds (handles both 's' and 'ms')
		const value = parseFloat(duration);
		return duration.includes('ms') ? value : value * 1000;
	}
}